import React, { useState, useEffect, useMemo } from 'react';
import { Navbar, Nav, Container } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { 
  FaHome,
  FaUser,
  FaCode,
  FaGraduationCap,
  FaBriefcase,
  FaCogs,
  FaProjectDiagram,
  FaBlog,
  FaComments,
  FaEnvelope,
  FaBars,
  FaMoon
} from 'react-icons/fa';

const Header = ({ currentTheme, toggleTheme }) => {
  const [activeSection, setActiveSection] = useState('hero');
  const [scrolled, setScrolled] = useState(false);
  const [showOffcanvas, setShowOffcanvas] = useState(false);

  const navItems = useMemo(() => [
    { id: 'hero', icon: FaHome, label: 'Home' },
    { id: 'about', icon: FaUser, label: 'About' },
    { id: 'skills', icon: FaCode, label: 'Skills' },
    { id: 'education', icon: FaGraduationCap, label: 'Education' },
    { id: 'experience', icon: FaBriefcase, label: 'Experience' },
    { id: 'service', icon: FaCogs, label: 'Services' },
    { id: 'projects', icon: FaProjectDiagram, label: 'Projects' },
    { id: 'blog', icon: FaBlog, label: 'Blog' },
    { id: 'testimonials', icon: FaComments, label: 'Testimonials' },
    { id: 'contact', icon: FaEnvelope, label: 'Contact' }
  ], []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      
      const sections = navItems.map(item => document.getElementById(item.id));
      const current = sections.find(section => {
        if (section) {
          const rect = section.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });

      if (current) {
        setActiveSection(current.id);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [navItems]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setActiveSection(sectionId);
      setShowOffcanvas(false);
    }
  };

  const NavContent = ({ mobile = false }) => (
    <>
      {navItems.map((item) => (
        <Nav.Link
          key={item.id}
          onClick={() => scrollToSection(item.id)}
          className={`
            d-flex align-items-center gap-2 px-3 py-2
            ${mobile ? 'mb-2' : 'mx-1'}
            position-relative nav-link-custom
            ${activeSection === item.id ? 'active' : ''}
          `}
          style={{ color: currentTheme.text }}
        >
          <item.icon style={{ color: activeSection === item.id ? currentTheme.primary : currentTheme.text }} />
          <span style={{ color: activeSection === item.id ? currentTheme.primary : currentTheme.text }}>
            {item.label}
          </span>
          {activeSection === item.id && !mobile && (
            <motion.div
              className="position-absolute bottom-0 left-0 w-100"
              style={{ height: '2px', background: currentTheme.primary }}
              layoutId="underline"
            />
          )}
        </Nav.Link>
      ))}
    </>
  );

  return (
    <Navbar 
      expand="lg" 
      fixed="top"
      className={`py-2 ${scrolled ? 'scrolled' : ''}`}
      style={{ background: `${currentTheme.background}CC` }}
    >
      <Container>
        <Navbar.Brand 
          href="#" 
          className="brand-logo"
          onClick={() => scrollToSection('hero')}
          style={{ color: currentTheme.primary }}
        >
          <span className="logo-text" style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700 }}>Sylvester</span>
        </Navbar.Brand>

        <div className="d-flex align-items-center">
          <button 
            onClick={toggleTheme} 
            className="theme-toggle-btn"
            style={{ 
              background: 'none', 
              border: 'none', 
              cursor: 'pointer', 
              color: currentTheme.text 
            }}
          >
            <FaMoon />
          </button>

          <Navbar.Toggle 
            aria-controls="basic-navbar-nav" 
            onClick={() => setShowOffcanvas(!showOffcanvas)}
          >
            <FaBars style={{ color: currentTheme.text }} />
          </Navbar.Toggle>
        </div>

        <Navbar.Collapse id="basic-navbar-nav" in={showOffcanvas}>
          <Nav className="ms-auto">
            <NavContent />
          </Nav>
        </Navbar.Collapse>
      </Container>

      <style jsx>{`
        .navbar {
          transition: all 0.3s ease;
          backdrop-filter: blur(10px);
        }

        .navbar.scrolled {
          padding-top: 0.5rem !important;
          padding-bottom: 0.5rem !important;
          box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
        }

        .brand-logo {
          font-size: 1.5rem;
          font-weight: 700;
          text-decoration: none;
          transition: all 0.3s ease;
        }

        .logo-text {
          background: linear-gradient(45deg, ${currentTheme.primary}, ${currentTheme.secondary});
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .nav-link-custom {
          font-size: 0.95rem;
          font-weight: 500;
          transition: all 0.3s ease;
          border-radius: 0.5rem;
        }

        .nav-link-custom:hover {
          background: ${currentTheme.name === 'Dark' ? 
            'rgba(255, 255, 255, 0.1)' : 
            'rgba(0, 0, 0, 0.05)'};
        }

        @media (max-width: 991.98px) {
          .navbar {
            padding-top: 0.5rem;
            padding-bottom: 0.5rem;
          }

          .nav-link-custom {
            padding: 0.75rem 1rem;
            border-radius: 0.5rem;
          }
        }
      `}</style>
    </Navbar>
  );
};

export default Header;